/**
 * @fileoverview App bar actions component with search, drawers, menus, and sign out.
 */

import * as React from 'react';
import { useState, type ReactElement } from 'react';
import { Search as SearchIcon } from '@mui/icons-material';
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { TbRobot } from 'react-icons/tb';
import { IoBookmark, IoBookmarkOutline, IoPricetags, IoPricetagsOutline, IoSettingsOutline } from 'react-icons/io5';
import { PiSignOut } from 'react-icons/pi';
import { useNavigate } from 'react-router';
import { signOut, useAuthSession } from '../../../features/auth';
import { SearchField, TooltipIconButton } from '@/shared/components';
// Imported directly (not via the features/notes barrel) so the app shell
// doesn't pull the whole notes feature — markdown rendering, highlight.js,
// note pages — into the entry chunk.
import { BookmarksDrawer } from '../../../features/notes/components/BookmarksDrawer';
import { TagsDrawer } from '../../../features/notes/components/TagsDrawer';
import { SearchDialog } from './SearchDialog';
import { ThemeSwitcher } from './ThemeSwitcher';
import { useAppBarState } from './hooks/useAppBarState';

export function AppBarActions(): ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const navigate = useNavigate();
  const { setSession } = useAuthSession();

  const {
    bookmarksOpen,
    tagsOpen,
    settingsAnchor,
    toggleBookmarks,
    closeBookmarks,
    toggleTags,
    closeTags,
    openSettings,
    closeSettings,
  } = useAppBarState();

  const handleNoteClick = (noteId: string): void => {
    closeBookmarks();
    navigate(`/${noteId}`);
  };

  const handleTagClick = (tagId: string, tagName: string): void => {
    closeTags();
    navigate(`/notes?tag=${encodeURIComponent(tagName)}`);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}&mode=hybrid`);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setSession(null);
    navigate('/sign-in');
  };

  const handleAISettingsNavigate = () => {
    closeSettings();
    navigate('/settings/ai');
  };

  return (
    <>
      <Stack direction="row">
        {/* Mobile search icon */}
        <TooltipIconButton
          tooltip="Search"
          type="button"
          aria-label="search"
          onClick={() => setSearchDialogOpen(true)}
          sx={{ display: { xs: 'inline', md: 'none' }, py: 0.1 }}
        >
          <SearchIcon sx={{ mt: 0.9 }} />
        </TooltipIconButton>

        {/* Desktop search field */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            onSubmit={handleSearchSubmit}
            placeholder="Search notes..."
            label="Search"
            sx={{ mr: 1, mt: 0.55 }}
          />
        </Box>

        {/* Tags button */}
        <Box sx={{ mt: 0.3 }}>
          <TooltipIconButton tooltip="Tags" type="button" aria-label="tags" onClick={toggleTags}>
            {tagsOpen ? <IoPricetags size={20} /> : <IoPricetagsOutline size={20} />}
          </TooltipIconButton>
        </Box>

        {/* Bookmarks button */}
        <Box sx={{ mt: 0.2 }}>
          <TooltipIconButton tooltip="Bookmarks" type="button" aria-label="bookmarks" onClick={toggleBookmarks}>
            {bookmarksOpen ? <IoBookmark size={20} /> : <IoBookmarkOutline size={20} />}
          </TooltipIconButton>
        </Box>

        <ThemeSwitcher />

        {/* Settings menu */}
        <Box sx={{ mt: 0.15 }}>
          <TooltipIconButton tooltip="Settings" type="button" aria-label="settings" onClick={openSettings}>
            <IoSettingsOutline size={20} />
          </TooltipIconButton>
          <Menu anchorEl={settingsAnchor} open={Boolean(settingsAnchor)} onClose={closeSettings}>
            <MenuItem onClick={handleAISettingsNavigate}>
              <ListItemIcon>
                <TbRobot size={20} />
              </ListItemIcon>
              <ListItemText>AI</ListItemText>
            </MenuItem>
          </Menu>
        </Box>

        {/* Sign out button */}
        <Box sx={{ mt: 0.15 }}>
          <TooltipIconButton tooltip="Sign Out" type="button" aria-label="sign out" onClick={handleSignOut}>
            <PiSignOut size={22} />
          </TooltipIconButton>
        </Box>
      </Stack>

      {/* Drawers and Dialogs */}
      <BookmarksDrawer open={bookmarksOpen} onClose={closeBookmarks} onNoteClick={handleNoteClick} />
      <TagsDrawer open={tagsOpen} onClose={closeTags} onTagClick={handleTagClick} />
      <SearchDialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} />
    </>
  );
}
